import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sheet from './Sheet';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LocaleContext';
import { useServiceCatalog } from '../hooks/useServiceCatalog';
import { displayName, vehicleTitle } from '../lib/format';
import { supabase } from '../lib/supabase';

const blankCar = { brand: '', model: '', year: '', plate: '' };
const blankPerson = { first: '', last: '', phone: '', email: '' };

/**
 * Open a job for a car that turned up without a booking
 * (create_walk_in_job). Finds the customer by name or phone, or adds them.
 */
export default function WalkInSheet({ onClose }) {
  const t = useT();
  const navigate = useNavigate();
  const { profile, isManager } = useAuth();
  const { catalog } = useServiceCatalog();
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [person, setPerson] = useState(blankPerson);
  const [vehicleId, setVehicleId] = useState('new');
  const [car, setCar] = useState(blankCar);
  const [service, setService] = useState('');
  const [problem, setProblem] = useState('');
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(profile?.branch_id || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    supabase
      .from('branches')
      .select('id, name')
      .order('name')
      .then(({ data }) => {
        setBranches(data || []);
        setBranchId((current) => current || data?.[0]?.id || '');
      });
  }, []);

  useEffect(() => {
    const needle = query.trim();
    if (needle.length < 3) {
      setMatches([]);
      return undefined;
    }
    let alive = true;
    const timer = setTimeout(async () => {
      const safe = needle.replace(/[%,()]/g, '');
      const { data } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name, phone, vehicles(id, brand, model, year, plate_number)')
        .eq('role', 'customer')
        .or(`full_name.ilike.%${safe}%,phone.ilike.%${safe}%`)
        .limit(8);
      if (alive) setMatches(data || []);
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [query]);

  function pickCustomer(row) {
    setCustomer(row);
    const cars = row?.vehicles || [];
    setVehicleId(cars[0]?.id || 'new');
  }

  async function submit(event) {
    event.preventDefault();
    setError('');
    if (!service.trim()) {
      setError(t('walkin.needService'));
      return;
    }
    if (!customer && (!person.phone.trim() || !person.first.trim())) {
      setError(t('walkin.needCustomer'));
      return;
    }
    if (vehicleId === 'new' && !car.brand.trim()) {
      setError(t('walkin.needCar'));
      return;
    }
    setSaving(true);
    const { data, error: rpcError } = await supabase.rpc('create_walk_in_job', {
      p_branch_id: branchId || null,
      p_service_type: service.trim(),
      p_problem: problem.trim() || null,
      p_customer_id: customer?.id || null,
      p_first_name: customer ? null : person.first.trim(),
      p_last_name: customer ? null : person.last.trim(),
      p_phone: customer ? null : person.phone.trim(),
      p_email: customer ? null : person.email.trim() || null,
      p_vehicle_id: vehicleId === 'new' ? null : vehicleId,
      p_brand: vehicleId === 'new' ? car.brand.trim() : null,
      p_model: vehicleId === 'new' ? car.model.trim() || null : null,
      p_year: vehicleId === 'new' && car.year ? Number(car.year) : null,
      p_plate: vehicleId === 'new' ? car.plate.trim() || null : null,
    });
    setSaving(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    onClose();
    navigate(`/bookings/${data}`);
  }

  const cars = customer?.vehicles || [];

  return (
    <Sheet title={t('walkin.title')} subtitle={t('walkin.subtitle')} onClose={onClose} variant="modal" wide>
      <form className="sheet-form walkin-form" onSubmit={submit}>
        <fieldset>
          <legend>{t('walkin.customer')}</legend>
          {customer ? (
            <div className="walkin-picked">
              <strong>{displayName(customer)}</strong>
              <span>{customer.phone || ''}</span>
              <button type="button" className="ghost" onClick={() => pickCustomer(null)}>
                {t('walkin.change')}
              </button>
            </div>
          ) : (
            <>
              <label>
                {t('walkin.find')}
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('walkin.findHint')} />
              </label>
              {matches.length ? (
                <ul className="walkin-matches">
                  {matches.map((row) => (
                    <li key={row.id}>
                      <button type="button" className="ghost" onClick={() => pickCustomer(row)}>
                        <strong>{displayName(row)}</strong> {row.phone || ''}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <p className="lede">{t('walkin.orNew')}</p>
              <div className="form-row">
                <label>
                  {t('walkin.firstName')}
                  <input value={person.first} onChange={(e) => setPerson({ ...person, first: e.target.value })} />
                </label>
                <label>
                  {t('walkin.lastName')}
                  <input value={person.last} onChange={(e) => setPerson({ ...person, last: e.target.value })} />
                </label>
              </div>
              <div className="form-row">
                <label>
                  {t('walkin.phone')}
                  <input value={person.phone} onChange={(e) => setPerson({ ...person, phone: e.target.value })} />
                </label>
                <label>
                  {t('walkin.email')}
                  <input type="email" value={person.email} onChange={(e) => setPerson({ ...person, email: e.target.value })} />
                </label>
              </div>
            </>
          )}
        </fieldset>

        <fieldset>
          <legend>{t('walkin.car')}</legend>
          {cars.length ? (
            <label>
              {t('walkin.whichCar')}
              <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
                {cars.map((item) => (
                  <option key={item.id} value={item.id}>
                    {[vehicleTitle(item), item.plate_number].filter(Boolean).join(' · ')}
                  </option>
                ))}
                <option value="new">{t('walkin.newCar')}</option>
              </select>
            </label>
          ) : null}
          {vehicleId === 'new' ? (
            <div className="form-row">
              <label>
                {t('walkin.brand')}
                <input value={car.brand} onChange={(e) => setCar({ ...car, brand: e.target.value })} />
              </label>
              <label>
                {t('walkin.model')}
                <input value={car.model} onChange={(e) => setCar({ ...car, model: e.target.value })} />
              </label>
              <label>
                {t('walkin.year')}
                <input inputMode="numeric" value={car.year} onChange={(e) => setCar({ ...car, year: e.target.value })} />
              </label>
              <label>
                {t('walkin.plate')}
                <input value={car.plate} onChange={(e) => setCar({ ...car, plate: e.target.value })} />
              </label>
            </div>
          ) : null}
        </fieldset>

        <fieldset>
          <legend>{t('walkin.job')}</legend>
          <label>
            {t('walkin.service')}
            <input list="walkin-services" value={service} onChange={(e) => setService(e.target.value)} />
            <datalist id="walkin-services">
              {catalog.map((item) => (
                <option key={item.id} value={item.name} />
              ))}
            </datalist>
          </label>
          <label>
            {t('walkin.problem')}
            <textarea rows={3} value={problem} onChange={(e) => setProblem(e.target.value)} />
          </label>
          {isManager && branches.length > 1 ? (
            <label>
              {t('walkin.branch')}
              <select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                {branches.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </fieldset>

        {error ? <p className="form-error">{error}</p> : null}
        <button type="submit" className="primary" disabled={saving}>
          {saving ? t('common.working') : t('walkin.open')}
        </button>
      </form>
    </Sheet>
  );
}
